import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
  OnInit,
  DestroyRef,
  ElementRef,
  HostListener
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import {
  WebSocketResilienceService,
  WebSocketConnectionState
} from '@core/services/websocket-resilience.service';
import { TooltipDirective } from '@shared/components/ui/tooltip/tooltip.directive';
import {
  VenueConnectionStripComponent,
  VenueRow,
  VenueState,
} from '@shared/components/venue-connection-strip';
import {
  ExchangeConnectivityService,
  ExchangeConnectionState as ExchangeRow,
} from '@features/system-health/services/exchange-connectivity.service';

/**
 * Global WebSocket Status Component
 *
 * Displays a compact indicator showing the overall WebSocket connection health
 * across the application. Designed to be placed in the header/navigation.
 *
 * Features:
 * - Visual indicator for connection state (connected/connecting/disconnected/error/degraded)
 * - Shows number of queued subscriptions if any
 * - Click to expand details panel
 * - Auto-hides when all connections healthy
 *
 * @example
 * ```html
 * <app-global-websocket-status></app-global-websocket-status>
 * ```
 */
@Component({
  selector: 'app-global-websocket-status',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective, VenueConnectionStripComponent],
  templateUrl: './global-websocket-status.component.html',
  styleUrls: ['./global-websocket-status.component.css']
})
export class GlobalWebSocketStatusComponent implements OnInit {
  private readonly resilienceService = inject(WebSocketResilienceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  /**
   * Per-venue connection state (GAP-FE-B003 / GAP-FU-03).
   * Read from `ExchangeConnectivityService` so the expanded panel can
   * surface a one-row-per-venue breakdown alongside the aggregate pill.
   * Service is `providedIn: 'root'` so importing it from `shared/` is
   * safe at runtime — see GAP-FE-C011 for the layer-cleanup plan
   * (relocate the service under `core/`).
   */
  private readonly exchangeConnectivity = inject(ExchangeConnectivityService);

  /**
   * Venue rows for the per-venue strip in the expanded panel.
   * Mirrors the derivation in
   * `features/system-health/components/health-overview` so both
   * surfaces stay in sync; consider extracting if a third surface needs it.
   */
  protected readonly venueRows = computed<readonly VenueRow[]>(() => {
    return this.exchangeConnectivity.exchangeList().map((ex: ExchangeRow): VenueRow => {
      const state: VenueState =
        ex.status === 'unknown' ? 'unknown' : ex.status === 'connected' ? 'online'
        : ex.status === 'connecting' ? 'connecting'
        : ex.status === 'degraded' ? 'degraded'
        : 'offline';
      return {
        name: ex.name.toUpperCase(),
        state,
        latencyMs: state === 'offline' ? null : ex.latencyMs,
        throughput: ex.throughput,
        lastSeenAt: ex.lastConnected ? ex.lastConnected.toISOString() : null,
        reason: ex.errorCount > 0 ? `${ex.errorCount} recent errors` : null,
      };
    });
  });

  // GAP-FE-038: timer() + takeUntilDestroyed replaces the manual setTimeout
  // tracking array. Each pending event cleanup now disposes automatically when
  // the component is destroyed — no need for an explicit `eventCleanupTimeouts`
  // bookkeeping array or `clearTimeout` calls in ngOnDestroy.

  // State signals from service
  readonly state = this.resilienceService.globalState;
  readonly isConnected = this.resilienceService.isConnected;
  readonly isConnecting = this.resilienceService.isConnecting;
  readonly hasError = this.resilienceService.hasError;
  readonly isDegraded = this.resilienceService.isDegraded;
  readonly queuedCount = this.resilienceService.queuedCount;

  // Local UI state
  private readonly _showDetails = signal(false);
  readonly showDetails = this._showDetails.asReadonly();

  private readonly _recentEvents = signal<Array<{
    type: string;
    endpoint: string;
    message: string;
    timestamp: Date;
  }>>([]);
  readonly recentEvents = this._recentEvents.asReadonly();

  // Computed display properties
  readonly statusIcon = computed(() => {
    const state = this.state();
    switch (state.status) {
      case 'connected':
        return 'connected';
      case 'connecting':
      case 'reconnecting':
        return 'connecting';
      case 'degraded':
        return 'degraded';
      case 'error':
        return 'error';
      case 'disconnected':
      default:
        return 'disconnected';
    }
  });

  readonly statusBgColor = computed(() => {
    const icon = this.statusIcon();
    switch (icon) {
      case 'connected':
        return 'bg-bb-accent-green/10';
      case 'connecting':
        return 'bg-bb-accent-amber/10';
      case 'degraded':
        return 'bg-bb-accent-amber/10';
      case 'error':
        return 'bg-bb-accent-red/10';
      case 'disconnected':
      default:
        return 'bg-bb-text-muted/10';
    }
  });

  readonly statusLabel = computed(() => {
    const state = this.state();
    switch (state.status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'reconnecting':
        return `Reconnecting (${state.reconnectAttempt}/${state.maxReconnectAttempts})`;
      case 'degraded':
        return 'Degraded (using fallback)';
      case 'error':
        return 'Connection Error';
      case 'disconnected':
      default:
        return 'Disconnected';
    }
  });

  readonly tooltipText = computed(() => {
    const state = this.state();
    const parts: string[] = [this.statusLabel()];

    if (state.queuedSubscriptions > 0) {
      parts.push(`${state.queuedSubscriptions} pending updates`);
    }

    if (state.lastConnected) {
      parts.push(`Last connected: ${this.formatTime(state.lastConnected)}`);
    }

    if (state.error) {
      parts.push('Error: connection issue');
    }

    return parts.join(' | ');
  });

  readonly shouldShow = computed(() => {
    // Always show if not connected, has queued items, or has recent events
    const state = this.state();
    return state.status !== 'connected' ||
           state.queuedSubscriptions > 0 ||
           this._recentEvents().length > 0;
  });

  ngOnInit(): void {
    // Subscribe to connection events
    this.resilienceService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this._recentEvents.update(events => {
          const newEvents = [
            {
              type: event.type,
              endpoint: this.sanitizeEndpoint(event.endpoint),
              message: event.type,
              timestamp: new Date()
            },
            ...events
          ].slice(0, 5); // Keep last 5 events
          return newEvents;
        });

        // Auto-clear events after 30 seconds.
        // GAP-FE-038: timer + takeUntilDestroyed replaces setTimeout — pending
        // cleanups are automatically cancelled when the component is destroyed.
        timer(30000)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this._recentEvents.update(events =>
              events.filter(e => e.timestamp.getTime() > Date.now() - 30000)
            );
          });
      });
  }

  toggleDetails(): void {
    this._showDetails.update(v => !v);
  }

  hideDetails(): void {
    this._showDetails.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this._showDetails() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.hideDetails();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._showDetails()) {
      this.hideDetails();
    }
  }

  /** Sanitize endpoint URLs to avoid exposing internal infrastructure */
  sanitizeEndpoint(endpoint: string): string {
    try {
      const url = new URL(endpoint);
      return url.pathname.split('/').pop() || 'endpoint';
    } catch {
      return 'endpoint';
    }
  }

  /** Sanitize raw error messages to avoid exposing infrastructure details */
  sanitizeError(error: string): string {
    if (!error) return '';
    if (error.length > 100) return 'Connection error — see logs for details';
    if (/wss?:\/\/|https?:\/\/|dns|tls|certificate|proxy|upstream|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|ECONNREFUSED|EHOSTUNREACH|ETIMEDOUT|ENOTFOUND|ECONNRESET|EADDRNOTAVAIL|handshake_failure|Bad Gateway|Service Unavailable|svc\.cluster\.local/i.test(error)) {
      return 'Connection error — unable to establish link';
    }
    return error;
  }

  clearEvents(): void {
    this._recentEvents.set([]);
  }

  formatTime(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    if (seconds < 3600) {
      return `${Math.floor(seconds / 60)}m ago`;
    }
    return `${Math.floor(seconds / 3600)}h ago`;
  }

  getEventTypeClass(type: string): string {
    switch (type) {
      case 'connected':
        return 'text-bb-accent-green';
      case 'disconnected':
        return 'text-bb-text-muted';
      case 'error':
        return 'text-bb-accent-red';
      case 'reconnecting':
        return 'text-bb-accent-amber';
      case 'degraded':
        return 'text-bb-accent-amber';
      default:
        return 'text-bb-text-muted';
    }
  }
}
